import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { login } from '@mysimpledb/shared/data/auth';
import { finishLogin } from '@mysimpledb/shared/data/auth/finishLogin';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button.web';
import '../styles/global.css';
import './LoginForm.css';

function LoginForm({ setUserId }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnTo = params.get('returnTo');
  console.log('RETURN TO', returnTo);

  const isNotesSite =
    window.location.hostname === 'mysimpledbnotes.com' ||
    window.location.hostname === 'www.mysimpledbnotes.com';

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(form.email)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    try {
      const res = await login(form.email, form.password);

      if (res?.require2fa) {
        navigate('/auth/2fa', {
          state: { challengeToken: res.challengeToken, email: form.email },
        });
        return;
      }

      if (returnTo) {
        await finishLogin(
          {
            token: res.token,
            userId: res.userId,
            email: form.email,
            productCode: res.productCode,
          },
          { navigate: () => {}, setUserId }
        );
        window.location.assign(decodeURIComponent(returnTo));
        return;
      }

      await finishLogin(
        {
          token: res.token,
          userId: res.userId,
          email: form.email,
          productCode: res.productCode,
        },
        { navigate, setUserId }
      );
    } catch (err) {
      console.error(err);

      const apiError =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message;

      if (apiError === 'EMAIL_NOT_VERIFIED') {
        // Send user to a dedicated public page that can resend verification
        navigate('/email-not-verified', { state: { email: form.email } });
        return;
      }

      setErrorMsg(
        "The info you entered doesn't match our records. Please try again or select Forgot Username or Password."
      );
    }
  };

  return (
    <div className="login-container">
      <div className="login-brand">
        {!isNotesSite ? (
          <>
            <div className="login-tagline">Information at Your Fingertips</div>

            <div className="login-subtagline">
              Secure • Organized • Always Available
            </div>
          </>
        ) : (
          <>
            <div className="login-note-tagline">
              Your notes—always there, never in the way.
            </div>
            <div className="login-notes-subtagline">
              Secure • Organized • Always Available
            </div>
          </>
        )}
      </div>

      <form className="login-form" onSubmit={handleSubmit}>
        {errorMsg && (
          <div className="login-error" role="alert" aria-live="polite">
            <span className="error-icon">!</span>
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="login-field">
          <label className="standard-label" htmlFor="email">
            Email
          </label>
          <input
            type="text"
            id="email"
            name="email"
            className="standard-input"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            inputMode="email"
            required
          />
        </div>

        <div className="login-field">
          <label className="standard-label" htmlFor="password">
            Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            className="standard-input"
            value={form.password}
            onChange={handleChange}
            autoComplete="current-password"
            required
          />
        </div>

        <div className="login-actions">
          <AppButton
            variant="solid"
            size="sm"
            type="submit"
            className="login-submit"
          >
            Sign In
          </AppButton>
        </div>
      </form>

      <div className="sign-up">
        <div>
          Not a{' '}
          <span className="my-simple-db">
            {isNotesSite ? 'MySimpleDB Notes' : 'MySimpleDB'}
          </span>{' '}
          member?{' '}
          <Link to="/create-account" className="signup-link">
            Sign Up Now
          </Link>
        </div>

        <div>
          <Link to="/forgot-password" className="signup-link forgot">
            Forgot Password?
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginForm;
